import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot } from '@angular/router';
import { User } from '@angular/fire/auth';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';
import { Observable, of } from 'rxjs';
import { switchMap, take, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthenticationService } from './authentication.service';

interface AdminProfile {
    admin?: boolean;
    schools?: string[];
}

@Injectable({
    providedIn: 'root'
})
export class AuthGuard implements CanActivate {
    private authService = inject(AuthenticationService);
    private firestore = inject(Firestore);

    canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        return this.authService.user$.pipe(
            take(1),
            switchMap(async (user) => {
                if (environment.isAdminPreview) {
                    return this.allowPreview(user, state.url);
                }
                if (user) {
                    return true;
                }
                try {
                    const newUser = await this.authService.signInAnonymously();
                    return !!newUser;
                } catch (error) {
                    console.error('Anonymous login failed:', error);
                    return false;
                }
            }),
            catchError((error) => {
                console.error('AuthGuard error:', error);
                return of(false);
            })
        );
    }

    private async allowPreview(user: User | null, routerUrl: string): Promise<boolean> {
        if (!user || user.isAnonymous) {
            this.redirectToAdminLogin(routerUrl);
            return false;
        }

        const schoolId = schoolIdFromRouterUrl(routerUrl);
        try {
            const snap = await getDoc(doc(this.firestore, `admin_users/${user.uid}`));
            if (!snap.exists()) {
                this.redirectToAdminLogin(routerUrl);
                return false;
            }
            const profile = snap.data() as AdminProfile;
            if (schoolId && !mayAccessSchool(profile, schoolId)) {
                window.location.assign('/no-access');
                return false;
            }
            return true;
        } catch (error) {
            console.error('Admin preview authorization failed:', error);
            this.redirectToAdminLogin(routerUrl);
            return false;
        }
    }

    private redirectToAdminLogin(routerUrl: string): void {
        const returnUrl = previewReturnUrl(routerUrl);
        const loginPath = loginHint(routerUrl) === 'support' ? '/support' : '/epl-login';
        window.location.assign(`${loginPath}?returnUrl=${encodeURIComponent(returnUrl)}`);
    }
}

function schoolIdFromRouterUrl(routerUrl: string): string {
    const path = routerUrl.split('?')[0].split('#')[0];
    const match = path.match(/^\/app\/([^/]+)/);
    return match ? decodeURIComponent(match[1]) : '';
}

function loginHint(routerUrl: string): string | null {
    const query = routerUrl.split('?')[1]?.split('#')[0] ?? '';
    return new URLSearchParams(query).get('login');
}

function previewReturnUrl(routerUrl: string): string {
    const pathAndQuery = routerUrl.split('#')[0];
    const relative = pathAndQuery.startsWith('/') ? pathAndQuery : `/${pathAndQuery}`;
    const url = new URL(relative, 'https://preview.local');
    url.searchParams.delete('login');
    const query = url.searchParams.toString();
    return `/preview${url.pathname}${query ? `?${query}` : ''}`;
}

function mayAccessSchool(profile: AdminProfile, schoolId: string): boolean {
    if (profile.admin === true) {
        return true;
    }
    return Array.isArray(profile.schools) && profile.schools.includes(schoolId);
}
